const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

console.log('🔧 API Service initialized. BASE_URL:', API_BASE_URL);

const apiCall = async (method, endpoint, data = null, includeUserId = false) => {
  const token = localStorage.getItem('token');
  const userId = localStorage.getItem('userId');
  const headers = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
    console.log(`[API] Token found: ${token.substring(0, 30)}...`);
  } else {
    console.warn(`[API] ⚠️  NO TOKEN FOUND IN LOCALSTORAGE`);
  }

  if (includeUserId && userId) {
    headers['user-id'] = userId;
  }

  const fullUrl = `${API_BASE_URL}${endpoint}`;
  console.log(`[API] ${method} ${fullUrl}`, { headers, data: data ? `<data object>` : 'none' });

  try {
    const response = await fetch(fullUrl, {
      method,
      headers,
      body: data ? JSON.stringify(data) : undefined,
    });

    console.log(`[API] Response status: ${response.status} ${response.statusText}`);

    if (!response.ok) {
      let errorBody = '';
      try {
        errorBody = await response.text();
        console.error(`[API] Error response:`, errorBody);
      } catch (e) {
        console.error(`[API] Error reading response body`);
      }
      throw new Error(`API Error ${response.status}: ${errorBody || response.statusText}`);
    }

    let jsonResponse;
    try {
      const responseText = await response.text();
      jsonResponse = responseText ? JSON.parse(responseText) : {};
    } catch (e) {
      console.error(`[API] Error parsing response:`, e);
      throw new Error('Invalid response format from server');
    }
    
    console.log(`[API] Response data:`, jsonResponse);
    return jsonResponse;
  } catch (error) {
    console.error('❌ API Error:', error.message);
    throw error;
  }
};

export const authService = {
  register: (phoneNumber, email, firstName, lastName, password, role) =>
    apiCall('POST', '/auth/register', { phoneNumber, email, firstName, lastName, password, role }),
  login: async (phoneNumber, password) => {
    // Special handling for login - send with minimal headers to avoid CORS preflight
    const fullUrl = `${API_BASE_URL}/auth/login`;
    
    console.log(`[API] POST ${fullUrl}`, { phoneNumber, password });
    
    try {
      const response = await fetch(fullUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phoneNumber, password }),
      });
      
      console.log(`[API] Response status:`, response.status, response.statusText);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[API] Error response body:`, errorText);
        throw new Error(`API Error: ${response.status} ${response.statusText} - ${errorText}`);
      }
      
      const jsonResponse = await response.json();
      console.log(`[API] Response data:`, jsonResponse);
      
      if (jsonResponse.token && jsonResponse.userId) {
        localStorage.setItem('userId', jsonResponse.userId);
      }
      return jsonResponse;
    } catch (error) {
      console.error('❌ API Error:', error);
      throw error;
    }
  },
  registerSeller: (phoneNumber, email, firstName, lastName, password, pan, gstin, bankAccount, ifsc, chequeUrl, state, region, farmName, latitude, longitude, areaInAcres, addressLine1, addressLine2, district, pincode, primaryCropType, farms) =>
    apiCall('POST', '/auth/seller/register', { 
      phoneNumber, email, firstName, lastName, password, 
      pan, gstin, 
      bankAccountNumber: bankAccount, ifscCode: ifsc, cancelledChequeUrl: chequeUrl, 
      state, region,
      farmName, latitude, longitude, areaInAcres, addressLine1, addressLine2, district, pincode,
      primaryCropType, 
      farms: farms || []
    }),
  registerBuyer: (phoneNumber, email, firstName, lastName, password, buyerType, companyName, businessCategory, state, region) =>
    apiCall('POST', '/auth/buyer/register', { phoneNumber, email, firstName, lastName, password, buyerType, companyName, businessCategory, state, region }),
  getUserProfile: (userId) =>
    apiCall('GET', `/auth/profile/${userId}`),
};

export const buyerService = {
  getListings: (cropType, state, region) =>
    apiCall('GET', `/buyer/listings?cropType=${cropType || ''}&state=${state || ''}&region=${region || ''}`),
  getListingDetails: (listingId) =>
    apiCall('GET', `/buyer/listings/${listingId}`),
  getMessages: () =>
    apiCall('GET', '/buyer/messages'),
  sendMessage: (senderId, receiverId, listingId, content) =>
    apiCall('POST', '/buyer/messages/send', { senderId, receiverId, listingId, content }),
  getTopSellerRecommendations: (topCount = 6) =>
    apiCall('GET', `/buyer/recommendations/sellers?topCount=${topCount}`),
  getSellerTopCrops: (sellerId, topCount = 3) =>
    apiCall('GET', `/buyer/recommendations/seller/${sellerId}/crops?topCount=${topCount}`),
  getSellerDetailsWithCrops: (sellerId) =>
    apiCall('GET', `/buyer/recommendations/seller/${sellerId}`),
  searchSellers: (searchTerm, limit = 10) =>
    apiCall('GET', `/buyer/search/sellers?searchTerm=${encodeURIComponent(searchTerm)}&limit=${limit}`),
};

export const sellerService = {
  createListing: (sellerId, cropType, breedVariety, farmingDetails, cultivationAreaInAcres, harvestQuantityInKg, minimumOrderQuantity, basePrice, certificationUrl, state, region, priceTiers) =>
    apiCall('POST', '/seller/listings/create', { sellerId, cropType, breedVariety, farmingDetails, cultivationAreaInAcres, harvestQuantityInKg, minimumOrderQuantity, basePrice, certificationUrl, state, region, priceTiers }, true),
  getListings: (sellerId) =>
    apiCall('GET', '/seller/listings', null, true),
  updateListing: (listingId, title, price, quantity, description, unitType) =>
    apiCall('PUT', `/seller/listings/${listingId}`, { title, price, quantity, description, unitType }, true),
  deleteListing: (listingId) =>
    apiCall('DELETE', `/seller/listings/${listingId}`, null, true),
  getDashboard: () =>
    apiCall('GET', '/seller/dashboard', null, true),
  getFarms: () =>
    apiCall('GET', '/seller/farms', null, true),
  getLiveCampaigns: () =>
    apiCall('GET', '/seller/campaigns/live', null, true),
  toggleCampaignUpvote: (campaignId, isInterested) =>
    apiCall('POST', `/seller/campaigns/${campaignId}/upvote`, { isInterested }, true),
};

export const adminService = {
  getDashboard: () =>
    apiCall('GET', '/admin/dashboard', null, true),
  getPendingListings: () =>
    apiCall('GET', '/admin/listings/pending', null, true),
  listAllListings: () =>
    apiCall('GET', '/admin/listings', null, true),
  approveListing: (listingId, adminId) =>
    apiCall('POST', '/admin/listings/approve', { listingId, adminId }, true),
  rejectListing: (listingId, adminId, rejectionReason) =>
    apiCall('POST', '/admin/listings/reject', { listingId, adminId, rejectionReason }, true),
};

export const registrationService = {
  registerSeller: (phoneNumber, email, firstName, lastName, password, pan, gstin, bankAccount, ifsc, chequeUrl, state, region, farmName, latitude, longitude, areaInAcres, addressLine1, addressLine2, district, pincode, primaryCropType, farms) => {
    // Extract crops from all farms to build the Crops array for backend validation
    const crops = [];
    if (farms && farms.length > 0) {
      farms.forEach(farm => {
        if (farm.crops && farm.crops.length > 0) {
          farm.crops.forEach(crop => {
            crops.push({
              cropType: crop.cropType,
              distributionPercentage: crop.distributionPercentage
            });
          });
        }
      });
    }
    
    return apiCall('POST', '/registration/seller/register', { 
      phoneNumber, email, firstName, lastName, password, 
      sellerName: `${firstName} ${lastName}`, // Combine for API
      pan, gstin, 
      bankAccountNumber: bankAccount, ifscCode: ifsc, cancelledChequeUrl: chequeUrl, 
      state, region,
      farmName, latitude, longitude, areaInAcres, addressLine1, addressLine2, district, pincode,
      primaryCropType, 
      crops: crops && crops.length > 0 ? crops : [], // Send extracted crops for validation
      farms: farms || []
    });
  },
  registerBuyer: (phoneNumber, email, firstName, lastName, password, buyerType, companyName, businessCategory, state, region, organizationName, addressLine1, addressLine2, district, pincode, pan, gst, panFile, gstFile) =>
    apiCall('POST', '/registration/buyer/register', { 
      phoneNumber, email, firstName, lastName, password, 
      buyerName: `${firstName} ${lastName}`, // Combine for API
      buyerType, companyName, businessCategory, 
      state, region,
      organizationName, addressLine1, addressLine2, district, pincode,
      pan, gst,
      panUrl: panFile ? panFile.name : null,
      gstUrl: gstFile ? gstFile.name : null
    }),
  getPendingSellerRegistrations: () =>
    apiCall('GET', '/registration/admin/pending-sellers', null, true),
  getPendingBuyerRegistrations: () =>
    apiCall('GET', '/registration/admin/pending-buyers', null, true),
  approveSellerRegistration: (registrationId, adminId) =>
    apiCall('POST', `/registration/admin/approve-seller/${registrationId}`, { adminId }, true),
  approveBuyerRegistration: (registrationId, adminId) =>
    apiCall('POST', `/registration/admin/approve-buyer/${registrationId}`, { adminId }, true),
  rejectSellerRegistration: (registrationId, reason) =>
    apiCall('POST', `/registration/admin/reject-seller/${registrationId}`, { reason }, true),
  rejectBuyerRegistration: (registrationId, reason) =>
    apiCall('POST', `/registration/admin/reject-buyer/${registrationId}`, { reason }, true),
  uploadRtcDocument: (sellerId, file) => {
    const formData = new FormData();
    formData.append('sellerId', sellerId);
    formData.append('rtcDocument', file);
    return apiCall('POST', '/registration/seller/rtc-document', formData, true, true);
  },
  addSellerFarm: (data) =>
    apiCall('POST', '/registration/seller/farm', data),
  uploadCertification: (data) =>
    apiCall('POST', '/registration/seller/certification', data),
  getSellerFarms: (sellerId) =>
    apiCall('GET', `/registration/seller/farms/${sellerId}`),
  getSellerCertifications: (sellerId) =>
    apiCall('GET', `/registration/seller/certifications/${sellerId}`),
  requestUnavailableCrop: (data) =>
    apiCall('POST', '/registration/buyer/crop-request', data),
  getBuyerRequests: (buyerId) =>
    apiCall('GET', `/registration/buyer/requests/${buyerId}`),
};
